import React, { useCallback, useMemo, useState } from "react";
import { Badge, Flex, Text } from "@chakra-ui/react";
import AppMyAccountCard from "@/features/my-account/app-my-account-card";
import AppButton from "@/features/line-sheet-sets/app-button";
import useIdentity from "@/features/ui/hooks/use-identity";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useListShippingInfos, {
  listShippingInfosTemplateUrl,
} from "@/features/my-account/hooks/use-list-shipping-infos";
import useCreateShippingInfo from "@/features/my-account/hooks/use-create-shipping-info";
import useUpdateShippingInfo from "@/features/my-account/hooks/use-update-shipping-info";
import useDeleteShippingInfo from "@/features/my-account/hooks/use-delete-shipping-info";
import ShippingInfoModal from "@/features/my-account/shipping-info-modal";
import { ShippingInfo } from "@/features/my-account/types/shipping-info.type";
import { mutate } from "swr";
import { calcUrl } from "@/features/ui/helpers/fetcher-helpers";

export default function ShippingInfoCard() {
  const identity = useIdentity();
  const companyId = identity?.company?.id;
  const { error: showError } = useAppToasts({ id: "GLOBAL" });

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ShippingInfo | undefined>(undefined);

  const parameter = useMemo(
    () => (companyId ? { companyId } : undefined),
    [companyId]
  );

  const { data, isLoading } = useListShippingInfos(parameter, {
    revalidateOnFocus: false,
  });

  const { fire: fireCreate } = useCreateShippingInfo();
  const { fire: fireUpdate } = useUpdateShippingInfo();
  const { fire: fireDelete } = useDeleteShippingInfo();

  const mutateList = useCallback(() => {
    if (companyId) {
      mutate({ url: calcUrl(listShippingInfosTemplateUrl, { companyId }) });
    }
  }, [companyId]);

  const handleCreate = useCallback(
    async (values: {
      alias: string;
      recipientName: string;
      contact: string;
      address: string;
      detailAddress: string;
      zipCode: string;
      isDefault: boolean;
    }) => {
      if (!companyId) return;
      try {
        await fireCreate({ companyId }, values);
        mutateList();
        setIsAddModalOpen(false);
      } catch (e) {
        showError(e);
      }
    },
    [companyId, fireCreate, mutateList, showError]
  );

  const handleUpdate = useCallback(
    async (values: {
      alias: string;
      recipientName: string;
      contact: string;
      address: string;
      detailAddress: string;
      zipCode: string;
      isDefault: boolean;
    }) => {
      if (!companyId || !editTarget) return;
      try {
        await fireUpdate({ companyId, shippingInfoId: editTarget.id }, values);
        mutateList();
        setEditTarget(undefined);
      } catch (e) {
        showError(e);
      }
    },
    [companyId, editTarget, fireUpdate, mutateList, showError]
  );

  const handleDelete = useCallback(
    async (id: number) => {
      if (!companyId) return;
      try {
        await fireDelete({ companyId, shippingInfoId: id }, {});
        mutateList();
      } catch (e) {
        showError(e);
      }
    },
    [companyId, fireDelete, mutateList, showError]
  );

  const infos = data ?? [];

  return (
    <>
      <AppMyAccountCard
        title={
          // Heading 안에서 width 100%가 없으면 이 Flex가 콘텐츠 폭만 차지해
          // space-between이 무효가 되고, 제목과 버튼이 붙은 채 헤더 폭을 넘긴다.
          <Flex
            alignItems={"center"}
            justifyContent={"space-between"}
            width={"100%"}
            gap={"8px"}
            flexWrap={"wrap"}
          >
            <Text fontSize={"18px"} fontWeight={"700"} minWidth={0}>
              배송지 관리
            </Text>
            <AppButton
              size={"small"}
              variant={"primary"}
              onClick={() => setIsAddModalOpen(true)}
            >
              배송지 추가
            </AppButton>
          </Flex>
        }
      >
        <Flex flexDirection={"column"} gap={"12px"}>
          {!isLoading && infos.length === 0 && (
            <Text
              fontSize={"12px"}
              color={"#8F8F8C"}
              textAlign={"center"}
              py={"8px"}
            >
              등록된 배송지가 없습니다.
            </Text>
          )}
          {infos.map((info) => (
            <Flex
              key={info.id}
              flexDirection={"column"}
              gap={"4px"}
              padding={"12px 16px"}
              borderRadius={"4px"}
              border={"0.5px solid #D4D4D1"}
            >
              <Flex alignItems={"center"} gap={"8px"} mb={"2px"}>
                <Text fontSize={"13px"} fontWeight={"600"}>
                  {info.alias}
                </Text>
                {info.isDefault && (
                  <Badge fontSize={"10px"} colorScheme={"blue"} borderRadius={"2px"}>
                    기본
                  </Badge>
                )}
              </Flex>
              <Text fontSize={"12px"} color={"#444440"}>
                {info.recipientName} · {info.contact}
              </Text>
              <Text fontSize={"12px"} color={"#6F6F6C"}>
                ({info.zipCode}) {info.address}{info.detailAddress ? ` ${info.detailAddress}` : ""}
              </Text>
              <Flex gap={"6px"} mt={"6px"}>
                <AppButton size={"small"} onClick={() => setEditTarget(info)}>
                  수정
                </AppButton>
                <AppButton size={"small"} onClick={() => handleDelete(info.id)}>
                  삭제
                </AppButton>
              </Flex>
            </Flex>
          ))}
        </Flex>
      </AppMyAccountCard>

      <ShippingInfoModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleCreate}
        title={"배송지 추가"}
      />

      {editTarget && (
        <ShippingInfoModal
          isOpen={true}
          onClose={() => setEditTarget(undefined)}
          onSubmit={handleUpdate}
          initialValues={editTarget}
          title={"배송지 수정"}
        />
      )}
    </>
  );
}
